import { useState } from 'react';
import { motion } from 'framer-motion';
import type { ContentProfile } from '@content-planner/shared';
import { useLeadCapture } from '../../hooks/useLeadCapture.js';

type Props = {
  profile: ContentProfile;
  month: string;
  onUnlock: (token: string, lead: { email: string; name?: string }) => void;
};

export function LeadCaptureGate({ profile, month, onUnlock }: Props) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const capture = useLeadCapture();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (honeypot) return; // bot — silently drop
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;
    const trimmedName = name.trim() || undefined;
    const res = await capture.submit({
      email: trimmedEmail,
      name: trimmedName,
      profile,
      month,
    });
    if (res.ok) onUnlock(res.token, { email: trimmedEmail, name: trimmedName });
  }

  return (
    <motion.aside
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="gcm-card border-2 border-gcm-brown my-6"
    >
      <span className="gcm-eyebrow">The rest of your month</span>
      <h3 className="mt-1 mb-2">Get weeks 2 through 4 delivered.</h3>
      <p className="text-text-secondary mb-4">
        Drop your email and we'll send the full month, all four weeks, straight to your inbox.
      </p>

      <form onSubmit={handleSubmit} className="grid gap-3">
        <label className="block">
          <span className="font-heading font-semibold text-sm">Email</span>
          <input
            type="email"
            required
            autoComplete="email"
            inputMode="email"
            className="gcm-input mt-1"
            placeholder="you@yourbusiness.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="font-heading font-semibold text-sm">
            First name <span className="text-text-muted font-normal">(optional)</span>
          </span>
          <input
            type="text"
            autoComplete="given-name"
            maxLength={120}
            className="gcm-input mt-1"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        {/* Honeypot — hidden from humans, attractive to bots */}
        <label className="hidden" aria-hidden="true">
          Leave this field empty
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </label>

        {capture.error && <p className="text-sm text-danger">{capture.error}</p>}

        <button
          type="submit"
          className="gcm-btn gcm-btn-primary mt-1"
          disabled={capture.status === 'submitting'}
        >
          {capture.status === 'submitting' ? 'Sending...' : 'Send my full plan'}
        </button>

        <p className="text-xs text-text-muted">
          One email, one plan. No newsletter spam. You can reply to opt out anytime.
        </p>
      </form>
    </motion.aside>
  );
}
